export enum TimeSlot {
  Morning = 1,
  Afternoon = 2,
  Evening = 3,
}

export enum ContactType {
  Owner = 1,
  Tenant = 2,
  Realtor = 3,
  Manager = 4,
}

export enum InspectionItemStatus {
  AguardandoDirecionamento = 0,
  AguardandoAceitePrestadora = 1,
  PrestadoraRecusou = 2,
  AguardandoDirecionamentoInspetor = 3,
  AguardandoAceiteInspetor = 4,
  InspetorRecusou = 5,
  AguardandoAgendamento = 6,
  Agendado = 7,
  CheckinRealizado = 8,
  Finalizado = 9,
  Cancelado = 10,
  Frustrado = 11,
  // Fluxo de revisão de laudos (ordem lógica)
  AguardandoGeracaoLaudo = 12,        // Checklist realizado, aguardando gerar laudo
  AguardandoRevisaoPrestadora = 13,   // Laudo gerado, prestadora revisa
  LaudoNegadoPorPrestadora = 14,      // Prestadora negou o laudo
  AguardandoRevisaoBanco = 15,        // Prestadora aprovou, banco revisa
  LaudoNegadoPorBanco = 16,           // Banco negou o laudo
  CorrecaoPeloInspetorBanco = 17,     // Inspetor do banco está corrigindo
  LaudoAceitoPeloBanco = 18,          // Banco aprovou (final)

  // Fluxo de televistoria
  TelevistoriaAgendada = 19,          // Televistoria agendada
  TelevistoriaFinalizada = 20,        // Televistoria finalizada

  // Fluxo de devolução e preenchimento direto
  LaudoDevolvido = 21,                // Laudo devolvido para correção (sistema externo ou revisão)
  AguardandoPreenchimentoPrestadora = 22  // Prestadora preenche diretamente (sem inspetor)
}

export enum InspectionIssueType {
  DificuldadeAgendamento = 1,
  ContatoNaoResponde = 2,
  EnderecoIncorreto = 3,
  DocumentacaoIncompleta = 4,
  AcessoNegado = 5,
  ImovelIndisponivel = 6,
  Outros = 99,
}

export enum InspectionIssueStatus {
  Aberta = 1,
  EmAndamento = 2,
  Resolvida = 3,
  Cancelada = 4,
}

export interface Phone {
  phoneNumber: string;
  phoneType: string;
}

export interface Proponent {
  name: string;
  cpfCnpj: string;
  email: string;
  phones: Phone[];
}

export interface Contact {
  contactName: string;
  email: string;
  contactType: ContactType;
  mobilePhone?: string;
  residentialPhone?: string;
  commercialPhone?: string;
}

export interface InspectionItemPayload {
  id?: string;
  inspectionReasonId: string;
  inspectionReasonName?: string;
  inspectionTypeId: string;
  inspectionTypeName?: string;
  propertyTypeId: string;
  propertyTypeName?: string;
  status?: number;
  statusDescription?: string;
  isAutomaticReport?: boolean;
  observation?: string;
  cep: string;
  street: string;
  complement?: string;
  number: string;
  district: string;
  state: string;
  city: string;
  iptuNumber?: string;
  registrationNumber?: string;
  registryOfficeNumber?: string;
  landArea?: number;
  builtArea?: number;
  privateArea?: number;
  contacts?: Contact[];
  schedules?: InspectionScheduleItem[];
  assignments?: InspectionAssignmentItem[];
  checklists?: any[];
  workflow?: InspectionWorkflow;
  issues?: InspectionIssue[];
  preliminaryCalculationsData?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InspectionScheduleItem {
  id: string;
  inspectionItemId: string;
  scheduleDate: string;
  timeSlot: number;
  timeSlotDescription: string;
  notes?: string;
  scheduledByUserId: string;
  createdAt: string;
  createdBy: string;
}

export interface InspectionAssignmentItem {
  id: string;
  inspectionItemId: string;
  providerId: string;
  providerName: string;
  providerAssignedUserId?: string;
  providerAssignedUserName?: string;
  inspectorId?: string;
  inspectorName?: string;
  inspectorCpf?: string;
  assignedByUserId: string;
  assignedAt: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
}

export interface InspectionWorkflow {
  statusWorkflow: StatusWorkflowItem[];
  assignmentWorkflow: AssignmentWorkflowItem[];
}

export interface StatusWorkflowItem {
  statusCode: number;
  statusDescription: string;
  action: string;
  performedByUserName: string;
  occurredAt: string;
  notes?: string;
}

export interface AssignmentWorkflowItem {
  action: string;
  fromUserName: string;
  fromUserRole: string;
  toName: string;
  toRole: string;
  occurredAt: string;
  notes?: string;
}

export interface Attachment {
  description: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
}

export interface CreateInspectionRequestPayload {
  proposalNumber: string;
  channel: string;
  /** Squad responsável pela proposta (enviado pelo Salesforce). Opcional. */
  squad?: string;
  isAutomaticReport?: boolean;
  proponents: Proponent[];
  items: InspectionItemPayload[];
  attachments?: Attachment[];
}

export interface InspectionRequest {
  id: string;
  proposalNumber: string;
  channel: string;
  /** Squad responsável pela proposta (enviado pelo Salesforce). Ausente em inspeções de outros canais. */
  squad?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  isFavorite: boolean;
  isAutomaticReport: boolean;
  proponents: Proponent[];
  items: InspectionItemPayload[];
  attachments?: Attachment[];
}

/**
 * Linha achatada da exportação do Panorama (GET /api/InspectionRequest/export).
 * Uma linha por item de inspeção, já com nomes e datas resolvidos pelo backend.
 */
export interface InspectionExportRow {
  requestId: string;
  itemId: string;
  proposalNumber?: string | null;
  /** Squad responsável pela proposta (Salesforce). Ausente em inspeções de outros canais. */
  squad?: string | null;
  createdAt: string;
  requestUpdatedAt?: string | null;
  inspectionTypeName: string;
  inspectionReasonName: string;
  propertyTypeName: string;
  proponentName?: string | null;
  proponentCpfCnpj?: string | null;
  proponentPhones: string[];
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  cep?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  iptuNumber?: string | null;
  registrationNumber?: string | null;
  registryOfficeNumber?: string | null;
  privateArea?: number | null;
  builtArea?: number | null;
  landArea?: number | null;
  statusCode: number;
  statusDescription: string;
  lastStatusAt?: string | null;
  reportDeliveredAt?: string | null;
  inspectionDoneAt?: string | null;
  providerReceivedAt?: string | null;
  providerName?: string | null;
  inspectorName?: string | null;
  inspectorCpf?: string | null;
  providerAssignedUserName?: string | null;
  /** Primeiro usuário que transmitiu o laudo do item (elaborador original). */
  reportAuthorName?: string | null;
  /**
   * Demais usuários que retransmitiram versões do laudo (correção, edição pós-aceite),
   * em ordem cronológica, separados por "; ". Vazio quando só houve um elaborador.
   */
  reportOtherTransmittersNames?: string | null;
  scheduleDate?: string | null;
  scheduleTimeSlot?: number | null;
}

/** Opção de "Inspetor" derivada dos direcionamentos ativos (GET /api/InspectionRequest/export/filter-options). */
export interface ExportInspectorOption {
  userId: string;
  name: string;
  cpf?: string | null;
  providerIds: string[];
  itemCount: number;
  isRegistered: boolean;
}

/** Opção de "Elaborador do Laudo" derivada das transmissões de laudo (mesmo endpoint). */
export interface ExportReportAuthorOption {
  userId: string;
  name: string;
  /** Inspetor | Prestadora | Banco | Inativo */
  origin: string;
  itemCount: number;
  isRegistered: boolean;
}

export interface ExportFilterOptions {
  inspectors: ExportInspectorOption[];
  reportAuthors: ExportReportAuthorOption[];
}

/** Resultado do endpoint /export: linhas + sinalização de truncamento pelo teto do backend. */
export interface InspectionExportResult {
  rows: InspectionExportRow[];
  /** true quando o backend cortou o resultado no teto de linhas (header X-Export-Truncated). */
  truncated: boolean;
  /** Teto de linhas do backend (header X-Export-Max-Rows), quando informado. */
  maxRows?: number;
}

export interface ScheduleInspectionPayload {
  scheduleDate: string;
  timeSlot: TimeSlot;
  notes?: string;
}

export interface InspectionSchedule {
  id: string;
  inspectionRequestId: string;
  scheduleDate: string;
  timeSlot: TimeSlot;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssignInspectionPayload {
  providerId: string;
  notes?: string;
}

export interface InspectionAssignment {
  id: string;
  inspectionRequestId: string;
  providerId: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionReason {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionItem {
  id: string;
  inspectionRequestId: string;
  inspectionReasonId: string;
  inspectionTypeId: string;
  propertyTypeId: string;
  status: InspectionItemStatus;
  statusDescription?: string;
  observation?: string;
  cep: string;
  street: string;
  complement?: string;
  number: string;
  district: string;
  state: string;
  city: string;
  iptuNumber?: string;
  registrationNumber?: string;
  registryOfficeNumber?: string;
  landArea?: number;
  builtArea?: number;
  privateArea?: number;
  preliminaryCalculationsData?: string;
  contacts?: Contact[];
  createdAt: string;
  updatedAt?: string;
}

export interface UpdateItemAddressPayload {
  cep: string;
  street: string;
  complement?: string;
  number: string;
  district: string;
  state: string;
  city: string;
}

export interface UpdateInspectionTypePayload {
  inspectionTypeId: string;
}

export interface PerformCheckinPayload {
  latitude: number;
  longitude: number;
  address: string;
}

export interface InspectionCheckin {
  id: string;
  inspectionRequestId: string;
  latitude: number;
  longitude: number;
  address: string;
  propertyLatitude?: number;
  propertyLongitude?: number;
  distanceFromPropertyMeters?: number;
  isWithinAllowedRadius: boolean;
  allowedRadiusMeters: number;
  timestamp: string;
  createdBy: string;
  createdAt: string;
}

export interface IssueAttachment {
  id?: string;
  inspectionIssueId?: string;
  fileName: string;
  fileUrl: string;
  bucketPath: string;
  bucketName: string;
  fileType: string;
  fileSizeBytes: number;
  uploadedBy?: string;
  uploadedAt?: string;
}

export interface IssueComment {
  id?: string;
  inspectionIssueId?: string;
  comment: string;
  commentedBy?: string;
  commentedByName?: string;
  commentedAt?: string;
  isInternal: boolean;
}

export interface CreateInspectionIssuePayload {
  inspectionItemId: string;
  type: InspectionIssueType;
  title: string;
  description: string;
  attachments?: IssueAttachment[];
}

export interface UpdateInspectionIssuePayload {
  title?: string;
  description?: string;
  status?: InspectionIssueStatus;
}

export interface ResolveInspectionIssuePayload {
  resolutionNotes: string;
}

export interface AddIssueCommentPayload {
  comment: string;
  isInternal: boolean;
}

export interface InspectionIssue {
  id: string;
  inspectionItemId: string;
  type: InspectionIssueType;
  typeDescription: string;
  status: InspectionIssueStatus;
  statusDescription: string;
  title: string;
  description: string;
  reportedBy: string;
  reportedByName?: string;
  reportedAt: string;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  resolutionNotes?: string;
  comments: IssueComment[];
  attachments: IssueAttachment[];
  createdAt: string;
  updatedAt?: string;
}

export interface GoogleCalendarStatus {
  isConnected: boolean;
  inspectorId: string;
  connectedAt?: string;
}

export interface CheckDuplicateLocationPayload {
  street: string;
  number: string;
  district: string;
  city: string;
  state: string;
  cep?: string;
}

export interface DuplicateInspection {
  inspectionItemId: string;
  inspectionRequestId: string;
  proposalNumber: string;
  address: string;
  statusDescription: string;
  createdAt: string;
}

export interface CheckDuplicateLocationResponse {
  hasDuplicate: boolean;
  duplicateInspections: DuplicateInspection[];
}
